import { strapiImageGetters } from './strapiImageGetters'
import type {
  StrapiEventListPage,
  EventListPageType,
} from '~/composables/types/strapiEventListPage'

const getId = (eventListPage: StrapiEventListPage): number | null => {
  return eventListPage?.id || null
}

const getSlug = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.attributes?.slug || ''
}

const getTitle = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.attributes?.title || null
}

const getDescription = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.attributes?.description || ''
}

const getSearchQuery = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.attributes?.searchQuery || null
}

const getSearchQueryObj = (eventListPage: StrapiEventListPage): any => {
  const query = {}

  const queryArr =
    eventListPage?.attributes?.searchQuery
      ?.split('&')
      .map((x) => x.split('=')) || []
  queryArr.forEach((x) => {
    query[x[0]] = x[1]
  })

  return queryArr.length > 0 ? query : null
}

const getType = (eventListPage: StrapiEventListPage): EventListPageType => {
  return eventListPage?.attributes?.type || null
}

const getSeoTitle = (eventListPage: StrapiEventListPage): string => {
  return `${eventListPage?.attributes?.seo?.metaTitle}`
}

const getSeoImageUrl = (
  eventListPage: StrapiEventListPage,
  format = null
): string => {
  return (
    strapiImageGetters.getImageUrl(
      eventListPage?.attributes?.seo?.metaImage?.data,
      format
    ) || null
  )
}

const getSeoMetaDescription = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.attributes?.seo?.metaDescription || null
}

const getCreatedAt = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.attributes?.createdAt || null
}

const getShowcase = (eventListPage: StrapiEventListPage): any => {
  return eventListPage?.attributes?.showcase || null
}

const getShowcaseTitle = (eventListPage: StrapiEventListPage): string => {
  return eventListPage?.attributes?.showcase?.title || null
}

const getHorizontalCardListTitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.attributes?.horizontalCardListTitle || null
}

const getHorizontalCardListLink = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.attributes?.horizontalCardListLink || null
}

const getHorizontalCardListLinkText = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.attributes?.horizontalCardListLinkText || null
}

const getHorizontalCardList = (eventListPage: StrapiEventListPage): any[] => {
  return eventListPage?.attributes?.horizontalCardList || []
}

const getGiftcardSection = (eventListPage: StrapiEventListPage): any => {
  return eventListPage?.attributes?.giftcardSection || null
}
const getGiftcardSectionTitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.attributes?.giftcardSection?.title || null
}

const getGiftcardSectionSectionTitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.attributes?.giftcardSection?.sectionTitle || null
}
const getGiftcardSectionSubtitle = (
  eventListPage: StrapiEventListPage
): string => {
  return eventListPage?.attributes?.giftcardSection?.subtitle || null
}

const getCitySlug = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.attributes?.citySlug || null
}

const getCategorySlug = (eventListPage: StrapiEventListPage): string | null => {
  return eventListPage?.attributes?.categorySlug || null
}

const getLocale = (eventListPage?: StrapiEventListPage): string | null => {
  return eventListPage?.attributes?.locale || null
}

const getSearchHeading = (
  eventListPage: StrapiEventListPage
): string | null => {
  return eventListPage?.attributes?.searchHeading || null
}

const getSearchSubtitle = (
  eventListPage: StrapiEventListPage
): string | null => {
  return eventListPage?.attributes?.searchSubtitle || null
}

const getLocalizations = (eventListPage: StrapiEventListPage): any[] => {
  return eventListPage?.attributes?.localizations?.data || null
}

const getLocalizationByLocale = (
  eventListPage: StrapiEventListPage,
  locale: string
): any => {
  if (!eventListPage) return null
  return (
    getLocalizations(eventListPage)?.find((obj) => {
      return getLocale(obj)?.toLowerCase() === locale
    }) || null
  )
}

const getSearchFilters = (eventListPage: StrapiEventListPage): any => {
  return eventListPage?.attributes?.searchFilters || null
}

const getKonfettiTeameventPageSlug = (
  eventListPage: StrapiEventListPage
): string => {
  return getSlug(eventListPage)?.split('-').splice(1).join('-')
}

const getKonfettiPagePath = (eventListPage: StrapiEventListPage): string => {
  const map = {
    city: `${getCitySlug(eventListPage)}`,
    category: `c/${getCategorySlug(eventListPage)}`,
    'city-category': `${getCitySlug(eventListPage)}/${getCategorySlug(
      eventListPage
    )}`,
    general: `t/${getSlug(eventListPage)}`,
    teamevent: `teamevent/${getKonfettiTeameventPageSlug(eventListPage)}`,
  }

  return `/${map[getType(eventListPage)]}`
}

export const strapiEventListPageGetters = {
  getId,
  getSlug,
  getCitySlug,
  getCategorySlug,
  getTitle,
  getDescription,
  getSearchQuery,
  getSearchQueryObj,
  getType,
  getSeoTitle,
  getSeoImageUrl,
  getSeoMetaDescription,
  getCreatedAt,
  getShowcase,
  getShowcaseTitle,
  getHorizontalCardList,
  getGiftcardSection,
  getGiftcardSectionTitle,
  getGiftcardSectionSectionTitle,
  getGiftcardSectionSubtitle,
  getHorizontalCardListTitle,
  getHorizontalCardListLink,
  getHorizontalCardListLinkText,
  getLocale,
  getSearchHeading,
  getSearchSubtitle,
  getLocalizations,
  getLocalizationByLocale,
  getSearchFilters,
  getKonfettiPagePath,
  getKonfettiTeameventPageSlug,
}
